import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { WalletSummaryResponse } from '@fetmode/types';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { formatCoins, formatUsdCents } from '@fetmode/utils';
import type { ShowSummary } from '@fetmode/types';

export interface StreamSessionToolsProps {
  liveShow: ShowSummary;
}

export function StreamSessionTools({ liveShow }: StreamSessionToolsProps): ReactElement {
  const { api } = useAuth();
  const [wallet, setWallet] = useState<WalletSummaryResponse | null>(null);

  useEffect(() => {
    let cancelled = false;

    void api
      .getWallet()
      .then((result) => {
        if (!cancelled) {
          setWallet(result);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <div className="fm-card stream-session-tools">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.live.sessionToolsTitle')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.live.sessionToolsHint')}
      </p>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem' }}>
        {t('common.studio.dashboard.viewerCount').replace('{{count}}', String(liveShow.viewerCount))}
        {(liveShow.spyCount ?? 0) > 0
          ? ` · ${t('common.studio.live.statsSpies')}: ${String(liveShow.spyCount)}`
          : ''}
      </p>
      {wallet?.earn ? (
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem' }}>
          {t('common.studio.dashboard.earnAvailable')}: {formatUsdCents(wallet.earn.availableCents)} ·{' '}
          {t('common.studio.dashboard.earnPending')}: {formatUsdCents(wallet.earn.pendingCents)}
        </p>
      ) : null}
      {wallet ? (
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
          {t('common.studio.dashboard.spendBalance')}: {formatCoins(wallet.spend.total)}
        </p>
      ) : null}
    </div>
  );
}

export function useStreamNavigationLock(isStreaming: boolean): void {
  useEffect(() => {
    if (!isStreaming) {
      return;
    }

    function handleBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      event.returnValue = '';
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isStreaming]);
}
